import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { WaterMappingRequest, WaterMappingResponse,WaterMappingDetailRequest,WaterMappingApiRequest,WaterMappingApiResponse,WaterMapping,SaveWaterMappingRequest,DeleteWaterMappingRequest,WaterMappingRow,DropdownColumn,WaterMappingCascadingFilterResponse,FilterColumn} from './interfaces/water-mapping-view.interfaces';
import { ErrorHandler } from '../utils/error-handler';
import { environment } from '../environments/environments';


@Injectable({
  providedIn: 'root',
})
export class WaterMappingViewService {
  private baseUrl = environment.WebApiURL;
  private readonly waterMappingExportTabName = 'WaterFlowNetwork';

  private dropdownUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterFlowNetworkDropDown`;
  }

  private detailsUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterFlowNetworkDetails`;
  }

  private filterUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterFlowNetworkFilters`;
  }

  private saveUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/InsertUpdateWaterFlowNetwork`;
  }

  private deleteUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/RemoveWaterFlowNetwork`;
  }

  private exportUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }

  constructor(private http: HttpClient) {}

  async exportAllWaterMappingData(): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl(), { tabName: this.waterMappingExportTabName }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const contentType = httpResponse.headers.get('content-type') || blob.type || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
    const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
    const normalizedContentType = contentType.toLowerCase();
    const inferredExtension =
      normalizedContentType.includes('spreadsheetml') ||
      normalizedContentType.includes('xlsx') ||
      decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
        ? '.xlsx'
        : '.csv';
    const fallbackFileName = `water-flow-network${inferredExtension}`;
    const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackFileName;
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
      ? decodedFileName
      : `${decodedFileName}${inferredExtension}`;

    return { blob, fileName };
  }

  // Map internal request to API request format - now direct pass-through
  private mapToApiRequest(request: WaterMappingDetailRequest): WaterMappingApiRequest {
    return {
      region: request.region,
      developmentArea: request.developmentArea,
      facilityName: request.facilityName,
      interConnection1: request.interConnection1,
      interConnection2: request.interConnection2,
      interConnection3: request.interConnection3,
      interConnection4: request.interConnection4,
      disposalOutletName: request.disposalOutletName,
      lastRowNumber: request.lastRowNumber
    };
  }

  // Map API response to internal format
  private mapFromApiResponse(apiResponse: WaterMappingApiResponse): {
    data: WaterMapping[];
    nextRowNumber: number | null;
  } {
    const mappedData: WaterMapping[] = apiResponse.waterItems.map(item => ({
      region: item.region,
      developmentArea: item.developmentArea,
      facilityName: item.facilityName,
      interConnection1: item.interConnection1,
      interConnection2: item.interConnection2 || '',
      interConnection3: item.interConnection3 || '',
      interConnection4: item.interConnection4 || '',
      disposalOutletName: item.disposalOutletName
    }));

    return {
      data: mappedData,
      nextRowNumber: apiResponse.nextRowNumber ?? null
    };
  }

  private parseDropdownResponse(response: DropdownColumn[]): WaterMappingResponse {
    const findColumn = (...names: string[]) =>
      response.find(item => names.includes((item.columnName || '').toLowerCase()));
    const unique = (values: string[]) => Array.from(new Set((values || []).filter(Boolean)));

    const regionItem = findColumn('region');
    const developmentAreaItem = findColumn('developmentarea');
    const developmentAreaPipelineItem = findColumn('developmentareapipeline');
    const subDevelopmentAreaItem = findColumn('subdevelopmentareaname', 'subdevelopmentarea');
    const facilityNameItem = findColumn('facilityname');
    const interconnection1Item = findColumn('interconnection1');
    const interconnection2Item = findColumn('interconnection2');
    const interconnection3Item = findColumn('interconnection3');
    const interconnection4Item = findColumn('interconnection4');
    const disposalOutletNameItem = findColumn('disposaloutletname');

    const developmentAreas = unique([
      ...(developmentAreaItem?.values || []),
      ...(developmentAreaPipelineItem?.values || []),
    ]);
    const subDevelopmentAreas = unique(subDevelopmentAreaItem?.values || []);

    return {
      regions: regionItem?.values || [],
      developmentAreas,
      subDevelopmentAreas,
      facilityNames: facilityNameItem?.values || [],
      interConnection1Options: interconnection1Item?.values || [],
      interConnection2Options: interconnection2Item?.values || [],
      interConnection3Options: interconnection3Item?.values || [],
      interConnection4Options: interconnection4Item?.values || [],
      disposalOutletNames: disposalOutletNameItem?.values || [],
    };
  }

  async getAllDropdownOptions(): Promise<WaterMappingResponse> {
    console.log('getAllDropdownOptions called');
    try {
      const requestBody: WaterMappingRequest = {
        region: null,
        developmentArea: null,
        facilityName: null,
        interConnection1: null,
        interConnection2: null,
        interConnection3: null,
        interConnection4: null,
        disposalOutletName: null
      };

      // No mapping needed - request body now matches API
      console.log('Dropdown API request payload:', requestBody);

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), requestBody)
      );      
      
      console.log('Raw dropdown API response:', response);
      const parsedResponse = this.parseDropdownResponse(response);
      console.log('Parsed dropdown response:', parsedResponse);
      return parsedResponse;
    } catch (error) {
      console.error('Dropdown API error:', error);
      ErrorHandler.handleError(error, 'WaterMappingViewService.getAllDropdownOptions');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async getDropdownOptions(requestBody: WaterMappingRequest): Promise<WaterMappingResponse> {
    try {
      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), requestBody)
      );
      const parsedResponse = this.parseDropdownResponse(response);
      return parsedResponse;
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterMappingViewService.getDropdownOptions');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async getWaterMappingData(filters?: Partial<WaterMappingDetailRequest>): Promise<{
    data: WaterMapping[];
    nextRowNumber: number | null;
  }> {
    console.log('getWaterMappingData called with filters:', filters);
    try {
      const payload: WaterMappingDetailRequest = {
        region: filters?.region || null,
        developmentArea: filters?.developmentArea || null,
        facilityName: filters?.facilityName || null,
        interConnection1: filters?.interConnection1 || null,
        interConnection2: filters?.interConnection2 || null,
        interConnection3: filters?.interConnection3 || null,
        interConnection4: filters?.interConnection4 || null,
        disposalOutletName: filters?.disposalOutletName || null,
        lastRowNumber: filters?.lastRowNumber ?? 0
      };

      console.log('Data API request payload:', payload);

      const response = await firstValueFrom(
        this.http.post<WaterMappingApiResponse>(this.detailsUrl(), payload)
      );
      
      console.log('Raw data API response:', response);
      
      const parsed = ErrorHandler.parseApiResult(response);
      if (parsed.success) {
        const mappedResponse = this.mapFromApiResponse(response);
        console.log('Mapped data response:', mappedResponse);
        return mappedResponse;
      } else {
        const errorMessage = parsed.message || 'Failed to fetch water mapping data';
        console.error('API returned non-success status:', parsed.statusCode, errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Data API error:', error);
      ErrorHandler.handleError(error, 'WaterMappingViewService.getWaterMappingData');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  // Update filter column mapping to use API field names directly
  private mapFilterColumnNameToField(columnName: string): string {
    const columnMap: { [key: string]: string } = {
      'region': 'region',
      'developmentArea': 'developmentArea',
      'subDevelopmentArea': 'developmentArea',
      'subDevelopmentAreaName': 'developmentArea',
      'developmentAreaPipeline': 'developmentArea',
      'facilityName': 'facilityName',
      'interConnection1': 'interConnection1',
      'interConnection2': 'interConnection2',
      'interConnection3': 'interConnection3',
      'interConnection4': 'interConnection4',
      'disposalOutletName': 'disposalOutletName'
    };

    return columnMap[columnName] || columnName.toLowerCase();
  }

  // Parse cascading filter response and map to internal structure
  private parseCascadingFilterResponse(response: FilterColumn[]): { [key: string]: string[] } {
    const filterData: { [key: string]: string[] } = {
      region: [],
      developmentArea: [],
      facilityName: [],
      interConnection1: [],
      interConnection2: [],
      interConnection3: [],
      interConnection4: [],
      disposalOutletName: []
    };

    if (response && response.length > 0) {
      response.forEach((column: FilterColumn) => {
        const fieldName = this.mapFilterColumnNameToField(column.columnName);
        if (filterData.hasOwnProperty(fieldName)) {
          filterData[fieldName] = Array.from(new Set([
            ...(filterData[fieldName] || []),
            ...(column.values || [])
          ]));
        }
      });
    }

    return filterData;
  }

  async getCascadingFilterOptions(filters?: Partial<WaterMappingRequest>): Promise<WaterMappingCascadingFilterResponse> {
    try {
      const payload: WaterMappingRequest = {
        region: filters?.region || null,
        developmentArea: filters?.developmentArea || null,
        facilityName: filters?.facilityName || null,
        interConnection1: filters?.interConnection1 || null,
        interConnection2: filters?.interConnection2 || null,
        interConnection3: filters?.interConnection3 || null,
        interConnection4: filters?.interConnection4 || null,
        disposalOutletName: filters?.disposalOutletName || null
      };

      // Filter panel options should come from filter endpoint.
      const response = await firstValueFrom(
        this.http.post<FilterColumn[]>(this.filterUrl(), payload)
      );

      return response || [];
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterMappingViewService.getCascadingFilterOptions');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  // Get filter options for UI components
  async getFilterOptionsForUI(filters?: Partial<WaterMappingRequest>): Promise<{ [key: string]: string[] }> {
    try {
      const response = await this.getCascadingFilterOptions(filters);
      return this.parseCascadingFilterResponse(response);
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterMappingViewService.getFilterOptionsForUI');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async saveWaterMapping(row: WaterMappingRow, action: 'INSERT' | 'UPDATE', userName: string, originalRow?: WaterMappingRow): Promise<any> {
    try {
      const body: SaveWaterMappingRequest = {
        action: action,
        oldInterConnection1: action === 'UPDATE' ? (originalRow?.interConnection1 ?? null) : null,
        oldInterConnection2: action === 'UPDATE' ? (originalRow?.interConnection2 ?? null) : null,
        oldInterConnection3: action === 'UPDATE' ? (originalRow?.interConnection3 ?? null) : null,
        oldInterConnection4: action === 'UPDATE' ? (originalRow?.interConnection4 ?? null) : null,
        oldDisposalOutletName: action === 'UPDATE' ? (originalRow?.disposalOutletName ?? null) : null,
        region: row.region || '',
        developmentArea: row.developmentArea || '',
        facilityName: row.facilityName || '',
        newInterConnection1: row.interConnection1 || null,
        newInterConnection2: row.interConnection2 || null,
        newInterConnection3: row.interConnection3 || null,
        newInterConnection4: row.interConnection4 || null,
        newDisposalOutletName: row.disposalOutletName || '',
        userName: userName
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.saveUrl(), body)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      console.error('Error saving water mapping:', error);
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async saveMultipleWaterMapping(rows: WaterMappingRow[], userName: string): Promise<any[]> {
    const results: any[] = [];
    
    for (const row of rows) {
      const action = row.isNew ? 'INSERT' : 'UPDATE';
      const originalRow = row.original as WaterMappingRow;
      
      const result = await this.saveWaterMapping(row, action, userName, originalRow);
      results.push(result);
    }

    return results;
  }

  async deleteWaterMapping(row: WaterMappingRow): Promise<any> {
    if (!row.region || !row.developmentArea || !row.facilityName || !row.disposalOutletName) {
      return {
        statusCode: 400,
        statusMessage: 'All identifiers (region, developmentArea, facilityName, disposalOutletName) are required for deletion.',
        error: true,
      };
    }

    try {
      const body: DeleteWaterMappingRequest = {
        region: row.region,
        developmentArea: row.developmentArea,
        facilityName: row.facilityName,
        interConnection1: row.interConnection1 || null,
        interConnection2: row.interConnection2 || null,
        interConnection3: row.interConnection3 || null,
        interConnection4: row.interConnection4 || null,
        disposalOutletName: row.disposalOutletName
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.deleteUrl(), body)
      );

      if (typeof response === 'string') {
        return {
          statusCode: 200,
          statusMessage: response,
          error: false,
        };
      }

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      console.error('Error deleting water mapping:', error);
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async deleteMultipleWaterMapping(rows: WaterMappingRow[]): Promise<any[]> {
    const results: any[] = [];
    
    for (const row of rows) {
      if (row.isNew) {
        continue;
      }
      
      const result = await this.deleteWaterMapping(row);
      results.push(result);
    }

    return results;
  }
}

export type { WaterMappingRequest,WaterMappingResponse,WaterMappingDetailRequest,WaterMappingApiRequest,WaterMappingApiResponse,WaterMapping,SaveWaterMappingRequest,DeleteWaterMappingRequest,WaterMappingRow, DropdownColumn,WaterMappingCascadingFilterResponse,FilterColumn} from './interfaces/water-mapping-view.interfaces';
